/**
 * Chooses vector (Clipper2) vs raster-silhouette measurement for SVG documents.
 * Hard clip-path → vector intersect branch. Soft mask / filter / embedded raster → raster.
 */

export type MeasureStrategy = 'vector' | 'raster-silhouette';

export type SvgMeasureProfile = {
  strategy: MeasureStrategy;
  hasHardClip: boolean;
  reasons: string[];
};

const RASTER_FORCE: Array<[RegExp, string]> = [
  [/<mask\b/i, 'mask definition'],
  [/\bmask\s*=\s*["']url\(/i, 'mask reference'],
  [/<filter\b/i, 'filter definition'],
  [/\bfilter\s*=\s*["']url\(/i, 'filter reference'],
  [/<(?:fe[A-Z][a-zA-Z]*)\b/i, 'filter primitive'],
  [/<image\b/i, 'embedded raster'],
  [/<foreignObject\b/i, 'foreignObject'],
];

const HARD_CLIP: Array<[RegExp, string]> = [
  [/<clipPath\b/i, 'clipPath definition'],
  [/\bclip-path\s*=\s*["']url\(/i, 'clip-path reference'],
  [/\bclip-path\s*:\s*url\(/i, 'clip-path style'],
];

function matchReasons(svg: string, patterns: Array<[RegExp, string]>): string[] {
  return patterns.filter(([re]) => re.test(svg)).map(([, reason]) => reason);
}

/** Fast string scan on full svgSource (defs included). */
export function profileSvgForMeasurement(svg: string): SvgMeasureProfile {
  const trimmed = svg?.trim() ?? '';
  if (!trimmed) {
    return { strategy: 'raster-silhouette', hasHardClip: false, reasons: ['empty svg'] };
  }

  const rasterReasons = matchReasons(trimmed, RASTER_FORCE);
  if (rasterReasons.length > 0) {
    return {
      strategy: 'raster-silhouette',
      hasHardClip: matchReasons(trimmed, HARD_CLIP).length > 0,
      reasons: rasterReasons,
    };
  }

  const clipReasons = matchReasons(trimmed, HARD_CLIP);
  return {
    strategy: 'vector',
    hasHardClip: clipReasons.length > 0,
    reasons: clipReasons.length > 0 ? clipReasons : ['simple vector'],
  };
}

/** Reject zero, NaN, or absurdly small/large cut lengths before accepting vector output. */
export function isPlausibleCutLength(
  perimeter: number,
  viewBoxWidth: number,
  viewBoxHeight: number
): boolean {
  if (!Number.isFinite(perimeter) || !(perimeter > 0)) return false;
  const minSide = Math.min(viewBoxWidth, viewBoxHeight);
  const maxSide = Math.max(viewBoxWidth, viewBoxHeight);
  if (!(minSide > 0) || !(maxSide > 0)) return perimeter > 0;
  const min = 2 * minSide * 0.01;
  const max = 2 * (viewBoxWidth + viewBoxHeight) * 500;
  return perimeter >= min && perimeter <= max;
}
